import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabaseClient';
import { LoadingSpinner } from '../../components/ui/LoadingState';
import { NotificationContainer } from '../../components/ui/NotificationToast';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

// Import sub-components
import Breadcrumb from './components/Breadcrumb';
import DetailsTab from './components/DetailsTab';
import MaintenanceTab from './components/MaintenanceTab';
import AttachmentsTab from './components/AttachmentsTab';
import AuditTab from './components/AuditTab';
import QRCodeModal from './components/QRCodeModal';
import WriteOffModal from '../../components/WriteOffModal';
import RefurbishModal from '../../components/RefurbishModal';
import AssignAssetModal from './components/AssignAssetModal';
import ReturnAssetModal from '../checkout-management/components/CheckInModal'; // Reusing CheckInModal as ReturnAssetModal
import { calculateEOLDate, getEOLStatus } from '../../utils/assetUtils';
import { useSelector } from 'react-redux';
import { logActivity } from '../../utils/activityLogger';

const AssetDetails = () => {
  const { t } = useTranslation();
  const { asset_tag } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State Management
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'details');
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  const location = useLocation();

  // Show any redirect message passed via router state (e.g. "disposed asset" error)
  useEffect(() => {
    if (location.state?.message) {
      setNotifications(prev => [...prev, { id: Date.now(), message: location.state.message, type: location.state.type || 'info' }]);
      // Clear the state so refreshing doesn't re-show it
      window.history.replaceState({}, document.title);
    }
  }, []);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showWriteOffModal, setShowWriteOffModal] = useState(false);
  const [showRefurbishModal, setShowRefurbishModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);

  // Data Payloads
  const [assetData, setAssetData] = useState(null);
  const [activeLoan, setActiveLoan] = useState(null);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [maintenanceData, setMaintenanceData] = useState([]);
  const [attachmentsData, setAttachmentsData] = useState([]);
  const [auditData, setAuditData] = useState([]);

  const { user } = useSelector((state) => state.auth);
  const userId = user?.id;
  const isPic = user?.role === 'pic' || user?.role === 'department_pic';
  const canDeleteAsset = user?.role === 'admin' || user?.role === 'system_admin' || user?.role === 'department_pic';

  // Assets with these statuses are considered permanently disposed and cannot be edited
  const DISPOSED_STATUSES = ['Written Off', 'Lost/Stolen'];
  const isDisposedAsset = assetData ? DISPOSED_STATUSES.includes(assetData.status) : false;

  // Fetch all required data for the asset
  const fetchAllData = async () => {
    if (!asset_tag) return;

    setIsLoading(true);
    try {
      // Parallel Fetch: Fire all queries at once to eliminate waterfalls
      const [
        { data: asset, error: assetError },
        { data: allLoans, error: historyError },
        { data: activities, error: auditError },
        { data: maintenance, error: maintError },
        { data: attachments, error: attachError }
      ] = await Promise.all([
        supabase
          .from('assets')
          .select('*, suppliers(company_name), locations(name), registered_by_profile:registered_by(full_name)')
          .eq('asset_tag', asset_tag)
          .single(),
        supabase
          .from('loans')
          .select('*, employees(full_name, email, departments(name)), departments(name), assigned_by_profile:assigned_by(full_name)')
          .eq('asset_tag', asset_tag)
          .order('checkout_date', { ascending: false }),
        supabase
          .from('activities')
          .select('*')
          .eq('asset_tag', asset_tag)
          .order('created_at', { ascending: false }),
        supabase
          .from('maintenance')
          .select('*, asset_attachments(*)')
          .eq('asset_tag', asset_tag)
          .order('maintenance_date', { ascending: false }),
        supabase
          .from('asset_attachments')
          .select('*')
          .eq('asset_tag', asset_tag)
          .order('created_at', { ascending: false })
      ]);

      if (assetError) throw assetError;
      if (historyError) throw historyError;

      // Detect active loan (Unreturned assignment)
      const active = allLoans?.find(l => !l.actual_return_date);

      if (asset) {
        if (active) {
          setActiveLoan({
            id: active.id,
            assetId: asset.asset_tag,
            assetName: asset.product_name,
            assetCategory: asset.category,
            checkoutDate: active.checkout_date,
            expectedReturnDate: active.expected_return_date,
            employee: active.employees ? {
              full_name: active.employees.full_name,
              department: active.employees.departments?.name
            } : null,
            assignedTo: active.departments ? {
              name: active.departments.name,
              department: active.departments.name
            } : null
          });
        } else {
          setActiveLoan(null);
        }

        setAssignmentHistory(allLoans || []);

        // Flatten/Format data for easier use in components
        const formattedAsset = {
          ...asset,
          supplier_name: asset.suppliers?.company_name || 'N/A',
          assigned_to_name: active?.employees?.full_name || (active?.departments?.name ? `Dept: ${active.departments.name}` : t('asset_details.unassigned')),
          assigned_to_email: active?.employees?.email || 'N/A',
          location_name: asset.locations?.name || t('asset_details.unassigned'),
          assignment_date: active?.checkout_date || null,
          assigned_by_name: active?.assigned_by_profile?.full_name || 'N/A'
        };
        setAssetData(formattedAsset);

        // Process Audit Trail
        if (!auditError) {
          const formattedAudit = activities.map(act => ({
            id: act.id,
            action: act.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            timestamp: act.created_at,
            user: 'System User', // Fallback as join is removed
            userRole: '',
            description: act.description,
            details: act.details
          }));
          setAuditData(formattedAudit);
        } else {
          console.error('Error fetching activities:', auditError);
        }

        // Set Maintenance records
        if (!maintError) {
          setMaintenanceData(maintenance || []);
        } else {
          console.error('Error fetching maintenance records:', maintError);
        }

        // Set Attachments records
        if (!attachError) {
          setAttachmentsData(attachments || []);
        } else {
          console.error('Error fetching attachments:', attachError);
        }
      } else {
        addNotification(t('asset_details.asset_not_found'), 'error');
      }
    } catch (error) {
      console.error('Error fetching asset detail:', error);
      addNotification(t('asset_details.load_error', { message: error.message }), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [asset_tag]);

  const handleMaintenanceSuccess = (message) => {
    addNotification(message || t('asset_details.maintenance_success'), 'success');
    fetchAllData();
  };

  const addNotification = (message, type = 'info') => {
    setNotifications(prev => [...prev, { id: Date.now(), message, type }]);
  };

  const handleEdit = () => {
    navigate(`/asset-registration?tag=${assetData.asset_tag}`);
  };

  const handlePrintQR = () => {
    setShowQRModal(true);
  };

  const handleWriteOffSuccess = (message) => {
    // Show success notification and redirect back to list
    navigate('/asset-list', { state: { message, type: 'success' } });
  };

  const isWriteOffDisabled = () => {
    if (!assetData) return true;
    
    // Explicit Rule: Active assets cannot be written off
    if (assetData.status === 'In Use') return true;
    
    // Existing Rule: status === 'Broken' OR EOL passed
    const isBroken = assetData.status === 'Broken';
    const eolDate = calculateEOLDate(assetData.purchase_date, assetData.lifespan_years || (assetData.lifespan_months / 12));
    const eolStatus = getEOLStatus(eolDate);
    const isExpired = eolStatus.status === 'Expired';
    
    return !(isBroken || isExpired);
  };

  const canBeRefurbished = () => {
    if (!assetData) return false;
    const isBroken = assetData.status === 'Broken';
    const isWrittenOff = assetData.status === 'Written Off';
    const eolDate = calculateEOLDate(assetData.purchase_date, assetData.lifespan_years || (assetData.lifespan_months / 12));
    const eolStatus = getEOLStatus(eolDate);
    const isExpired = eolStatus.status === 'Expired';
    
    return isBroken || isWrittenOff || isExpired;
  };

  const getWriteOffTooltip = () => {
    if (!assetData) return '';
    if (assetData.status === 'In Use') return t('asset_details.writeoff_return_first');
    if (!isWriteOffDisabled()) return t('asset_details.writeoff_decommission');
    return t('asset_details.writeoff_restricted');
  };

  const handleStatusAction = async (e) => {
    const isInUse = ['In Use', 'overdue'].includes(assetData?.status);
    console.log("Status action triggered. Current status:", assetData?.status, "Is in use:", isInUse);
    
    if (isInUse) {
      if (!activeLoan) {
        console.warn("Ghost state detected! Auto-healing asset...");
        addNotification(t('asset_details.fixing_ghost_data'), "info");

        try {
          const { error } = await supabase
            .from('assets')
            .update({ status: 'Available' }) // Force back to Available
            .eq('asset_tag', assetData.asset_tag);

          if (error) throw error;

          addNotification(t('asset_details.asset_auto_fixed'), "success");
          fetchAllData(); // Refresh the page data
        } catch (error) {
          console.error("Failed to auto-heal:", error);
          addNotification(t('asset_details.fix_status_error', { message: error.message }), "error");
        }
        return; // Stop execution so the modal doesn't open
      }
      
      console.log("Opening Return Modal. activeLoan is:", activeLoan);
      setShowReturnModal(true);
    } else {
      console.log("Opening Assign Modal");
      setShowAssignModal(true);
    }
  };

  const handleReturnSuccess = async (loanId, condition, notes) => {
    try {
      const { error: loanError } = await supabase
        .from('loans')
        .update({
          status: 'returned',
          actual_return_date: new Date().toISOString(),
          notes: notes,
        })
        .eq('id', loanId);

      if (loanError) throw loanError;

      // 1. Strictly map the condition to the approved status
      let safeStatus = 'Available'; 
      if (condition === 'Damaged') {
        safeStatus = 'In Repair';
      } else if (condition === 'Broken') {
        safeStatus = 'Broken';
      } else if (condition === 'Good') {
        safeStatus = 'Available';
      }

      console.log("SENDING TO SUPABASE -> Status:", safeStatus);

      // 2. Execute the Supabase update using the mapped status
      const { error: assetError } = await supabase
        .from('assets')
        .update({ 
          status: safeStatus,  // CRITICAL: Use safeStatus, NOT the raw condition
          condition: condition,      // Update condition column as well
          current_department_id: null 
        })
        .eq('asset_tag', assetData.asset_tag);

      if (assetError) throw assetError;

      // Log activity
      await logActivity(
        'asset_returned',
        `Asset ${assetData.product_name} (${assetData.asset_tag}) returned with condition: ${condition}`,
        assetData.asset_tag,
        userId,
        { condition: condition, notes: notes, status: safeStatus }
      );

      addNotification('Asset successfully returned', 'success');
      setShowReturnModal(false);
      fetchAllData();
    } catch (error) {
      console.error('Return error:', error);
      addNotification(`Failed to return asset: ${error.message}`, 'error');
      throw error; // Re-throw so the modal can handle it
    }
  };

  const handleRefurbishSuccess = (message) => {
    addNotification(message, 'success');
    fetchAllData();
  };

  const handleRefurbish = async () => {
    const confirmRevive = window.confirm("Are you sure you want to mark this broken asset as fully repaired and Available?");
    if (!confirmRevive) return;

    addNotification('Reviving asset...', 'info');
    try {
      const { error } = await supabase
        .from('assets')
        .update({ status: 'Available' })
        .eq('asset_tag', assetData.asset_tag);

      if (error) throw error;

      await logActivity(
        'asset_refurbished',
        `Asset ${assetData.product_name} (${assetData.asset_tag}) refurbished and set to Available`,
        assetData.asset_tag,
        userId,
        {}
      );

      addNotification('Asset successfully refurbished and is now Available!', 'success');
      fetchAllData();
    } catch (error) {
      console.error('Failed to revive asset:', error);
      addNotification('Failed to refurbish the asset.', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (!assetData) {
    return (
      <div className="p-12 text-center">
        <Icon name="AlertCircle" size={48} className="mx-auto text-error mb-4" />
        <h2 className="text-xl font-bold text-foreground mb-2">{t('asset_details.not_found_title')}</h2>
        <p className="text-muted-foreground mb-6">{t('asset_details.asset_tag_missing', { tag: asset_tag })}</p>
        <Button onClick={() => navigate('/asset-list')}>{t('asset_details.back_to_asset_list')}</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <div className="px-1">
        <Breadcrumb asset={assetData} />
      </div>

      {/* Asset Title Row / Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-card p-6 rounded-xl border border-border shadow-sm">
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center text-primary border border-primary/20 shadow-sm">
            <Icon name="Package" size={32} />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{assetData.product_name}</h1>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${
                assetData.status === 'Available' ? 'bg-green-50 text-green-700 border-green-200' :
                assetData.status === 'In Use' ? 'bg-primary/10 text-primary border-primary/30' :
                'bg-muted text-muted-foreground border-border'
              }`}>
                {assetData.status?.replace(/_/g, ' ')}
              </span>
            </div>
            <p className="text-muted-foreground mt-1 font-medium flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-muted-foreground/70">Asset Tag:</span>
              <span className="font-mono text-foreground/80">{assetData.asset_tag}</span>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" iconName="Printer" onClick={handlePrintQR} className="rounded-lg">{t('asset_details.print_qr')}</Button>
          {isDisposedAsset && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs font-semibold">
              <Icon name="Lock" size={13} />
              Read-Only — Asset Disposed
            </span>
          )}
          {!isPic && !isDisposedAsset && (
            <>
              <Button variant="outline" iconName="Edit" onClick={handleEdit} className="rounded-lg">{t('asset_details.edit_asset')}</Button>
              
              {assetData?.status === 'Broken' && (
                <Button
                  variant="outline"
                  className="border-amber-200 text-amber-700 hover:bg-amber-50 rounded-lg"
                  iconName="Wrench"
                  onClick={handleRefurbish}
                >
                  {t('asset_details.refurbish')}
                </Button>
              )}
              {canDeleteAsset && (
                <Button 
                  variant="outline" 
                  className={`border-red-200 text-red-600 hover:bg-red-50 rounded-lg ${isWriteOffDisabled() ? 'opacity-50 cursor-not-allowed' : ''}`}
                  iconName="Trash2" 
                  onClick={() => !isWriteOffDisabled() && setShowWriteOffModal(true)}
                  title={getWriteOffTooltip()}
                >
                  {t('asset_details.write_off')}
                </Button>
              )}

              {assetData?.status === 'Available' ? (
                <Button
                  variant="outline"
                  iconName="UserPlus"
                  onClick={() => setShowAssignModal(true)}
                  className="rounded-lg border-blue-200 text-blue-700 hover:bg-blue-50"
                >
                  {t('asset_details.assign_asset')}
                </Button>
              ) : assetData?.status === 'In Use' ? (
                <Button
                  variant="outline"
                  iconName="UserMinus"
                  onClick={() => setShowReturnModal(true)}
                  className="rounded-lg border-blue-200 text-blue-700 hover:bg-blue-50"
                >
                  {t('asset_details.return_asset')}
                </Button>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Tabbed Interface */}
      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        {/* Tab Navigation Bar */}
        <div className="border-b border-border bg-muted/50">
          <nav className="flex overflow-x-auto no-scrollbar">
            {[
              { id: 'details', label: 'Overview', icon: 'Info' },
              { id: 'maintenance', label: 'Maintenance', icon: 'Wrench' },
              { id: 'attachments', label: 'Attachments', icon: 'Paperclip' },
              { id: 'audit', label: 'History', icon: 'History' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-8 py-5 text-sm font-bold border-b-2 transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'border-blue-600 text-primary bg-card' 
                    : 'border-transparent text-muted-foreground hover:text-gray-900 hover:bg-muted/50/50'
                }`}
              >
                <Icon name={tab.icon} size={18} className={activeTab === tab.id ? 'text-primary' : 'text-muted-foreground/70'} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content Area */}
        <div className="p-0">
          <div className="p-6 md:p-8">
            {activeTab === 'details' && <DetailsTab asset={assetData} assignmentHistory={assignmentHistory} maintenanceHistory={maintenanceData} />}
            {activeTab === 'maintenance' && (
              <MaintenanceTab 
                assetTag={asset_tag} 
                maintenanceHistory={maintenanceData} 
                onSuccess={handleMaintenanceSuccess} 
              />
            )}
            {activeTab === 'attachments' && (
              <AttachmentsTab 
                assetTag={asset_tag}
                attachments={attachmentsData} 
                onSuccess={() => handleMaintenanceSuccess('Attachments updated successfully')}
              />
            )}
            {activeTab === 'audit' && <AuditTab auditTrail={auditData} asset={assetData} />}
          </div>
        </div>
      </div>

      {/* Modals & Notifications */}
      {showQRModal && (
        <QRCodeModal
          asset={assetData}
          onClose={() => setShowQRModal(false)}
        />
      )}

      {showWriteOffModal && (
        <WriteOffModal
          asset={assetData}
          isOpen={showWriteOffModal}
          onClose={() => setShowWriteOffModal(false)}
          onSuccess={handleWriteOffSuccess}
        />
      )}

      {showRefurbishModal && (
        <RefurbishModal
          asset={assetData}
          isOpen={showRefurbishModal}
          onClose={() => setShowRefurbishModal(false)}
          onSuccess={handleRefurbishSuccess}
        />
      )}

      {showAssignModal && (
        <AssignAssetModal
          asset={assetData}
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          onSuccess={(msg) => {
            addNotification(msg, 'success');
            fetchAllData();
          }}
        />
      )}

      {showReturnModal && activeLoan && (
        <ReturnAssetModal
          loan={activeLoan}
          onCheckIn={handleReturnSuccess}
          onClose={() => setShowReturnModal(false)}
        />
      )}

      <NotificationContainer 
        notifications={notifications} 
        onRemove={(id) => setNotifications(prev => prev.filter(n => n.id !== id))} 
      />
    </div>
  );
};

export default AssetDetails;

